import React, { useEffect, useRef, useState } from 'react'
import { Paper, Slider, Stack } from '@mui/material'

import { Icon, Typography } from '~components/internal'
import IconButton from '~components/internal/IconButton'
import { spacing, typography } from '~tokens/internal'

import './AudioPlayer.scss'

interface AudioPlayerProps {
  audioUrl: string
  onError?: (error: Error) => void
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({ audioUrl, onError }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const animationRef = useRef<number | null>(null)
  const isSeekingRef = useRef(false)

  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [isMuted, setIsMuted] = useState(false)

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const updateTime = (): void => {
      if (!isSeekingRef.current) {
        const newTime = audio.currentTime
        setCurrentTime(newTime)
      }

      if (isPlaying) {
        animationRef.current = requestAnimationFrame(updateTime)
      }
    }

    if (isPlaying) {
      animationRef.current = requestAnimationFrame(updateTime)
    }

    return (): void => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [isPlaying])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const handleLoadedMetadata = (): void => {
      setDuration(audio.duration)
    }

    const handleEnded = (): void => {
      setIsPlaying(false)
      setCurrentTime(0)
    }

    const handleError = (e: Event): void => {
      console.error('Audio playback error:', e)
      if (onError) {
        onError(new Error('Audio playback failed'))
      }
    }

    audio.addEventListener('loadedmetadata', handleLoadedMetadata)
    audio.addEventListener('ended', handleEnded)
    audio.addEventListener('error', handleError)

    return (): void => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
      audio.removeEventListener('ended', handleEnded)
      audio.removeEventListener('error', handleError)
    }
  }, [onError])

  const togglePlayPause = (): void => {
    const audio = audioRef.current
    if (!audio) return

    if (isPlaying) {
      audio.pause()
      setIsPlaying(false)
    } else {
      audio.play()
      setIsPlaying(true)
    }
  }

  const handleSliderChangeCommitted = (
    _event: React.SyntheticEvent | Event,
    value: number | number[],
  ): void => {
    const audio = audioRef.current
    if (!audio) return

    isSeekingRef.current = false
    const newTime = value as number
    audio.currentTime = newTime
    setCurrentTime(newTime)
  }

  const handleSliderChange = (_event: Event, value: number | number[]): void => {
    isSeekingRef.current = true
    setCurrentTime(value as number)
  }

  const toggleMute = (): void => {
    const audio = audioRef.current
    if (!audio) return

    audio.muted = !isMuted
    setIsMuted(!isMuted)
  }

  return (
    <Paper elevation={0} className='internal-audio-player'>
      <audio ref={audioRef} src={audioUrl} preload='metadata' />

      <Stack spacing={spacing.compact}>
        <Stack direction='row' sx={{ alignItems: 'center' }} spacing={spacing.xs}>
          <IconButton onClick={togglePlayPause} size='small' variant='contained' color='primary'>
            {isPlaying ? (
              <Icon name='Pause' size={typography.fontSizePx.lg} />
            ) : (
              <Icon name='PlayArrow' size={typography.fontSizePx.lg} />
            )}
          </IconButton>

          <Typography variant='caption' className='internal-audio-player__time'>
            {formatTime(currentTime)}
          </Typography>

          <Slider
            value={currentTime}
            max={duration || 100}
            onChange={handleSliderChange}
            onChangeCommitted={handleSliderChangeCommitted}
            size='small'
            color='primary'
            className='internal-audio-player__slider'
          />

          <Typography variant='caption' className='internal-audio-player__time'>
            {formatTime(duration)}
          </Typography>

          <IconButton onClick={toggleMute} size='small'>
            {isMuted ? (
              <Icon name='VolumeOff' size={typography.fontSizePx.lg} />
            ) : (
              <Icon name='VolumeUp' size={typography.fontSizePx.lg} />
            )}
          </IconButton>
        </Stack>
      </Stack>
    </Paper>
  )
}

export default AudioPlayer
